import { useCallback, useState, useEffect } from 'react'
import { safeGetLogger } from '@restaurant/logging'
import {
  isValidEmail,
  isValidPhone,
  isValidZipCode,
  formatPhone,
  getPhoneDigits,
  detectCardType,
  isValidCardLength,
  stripCardFormatting,
  isValidLuhn,
  type CreditCardType
} from '@restaurant/core'
import type { IPlatformServices } from '../contracts'
import {
  ValidationPreferencesSchema,
  type FieldValidation,
  type PhoneFieldValidation,
  type PaymentCardValidation,
  type ValidationSummary,
  type FormValidation,
  type ValidationPreferences,
  type AddressFields,
  type PaymentFields
} from './ValidationSchemas'

// State interfaces
export interface ValidationViewModelState {
  // Individual field validations
  phoneValidation: PhoneFieldValidation
  emailValidation: FieldValidation
  
  // Address validation
  addressValidation: {
    street: FieldValidation
    apartment?: FieldValidation
    city: FieldValidation
    state: FieldValidation
    zipCode: FieldValidation
    isComplete: boolean
  }
  
  // Payment validation
  paymentValidation: {
    cardNumber: PaymentCardValidation
    expiryMonth: FieldValidation
    expiryYear: FieldValidation
    cvv: FieldValidation
    isComplete: boolean
    isExpiryValid: boolean
  }
  
  // Form-level validation
  formValidation: FormValidation
  
  // Validation preferences
  validationPreferences: ValidationPreferences
}

export interface ValidationViewModelActions {
  // Field validation methods
  validatePhone: (phone: string) => void
  validateEmail: (email: string) => void
  validateAddress: (address: Partial<AddressFields>) => void
  validatePayment: (payment: Partial<PaymentFields>) => void
  
  // Form validation orchestration
  validateForm: (formData: any) => void
  resetFormValidation: () => void
  getValidationSummary: () => ValidationSummary
  
  // Field-specific utilities
  getFieldError: (fieldName: string) => string | null
  isFieldValid: (fieldName: string) => boolean
  isFieldTouched: (fieldName: string) => boolean
  
  // Preferences management
  setValidationPreferences: (preferences: Partial<ValidationPreferences>) => void
}

export interface ValidationViewModel extends ValidationViewModelState, ValidationViewModelActions {}

// Helper functions
const createFieldValidation = <T = string>(
  value: T = '' as T, 
  isValid = false, 
  error: string | null = null, 
  touched = false
): FieldValidation<T> => ({
  value,
  isValid,
  error,
  touched,
})

const createPhoneFieldValidation = (
  value = '', 
  isValid = false, 
  error: string | null = null, 
  touched = false,
  formatted = '',
  rawValue = '',
  country?: string
): PhoneFieldValidation => ({
  value,
  isValid,
  error,
  touched,
  formatted,
  rawValue,
  country,
})

const createPaymentCardValidation = (
  value = '', 
  isValid = false, 
  error: string | null = null, 
  touched = false,
  brand?: string,
  last4?: string
): PaymentCardValidation => ({
  value,
  isValid,
  error,
  touched,
  brand,
  last4,
})


/**
 * Platform-agnostic ValidationViewModel factory
 * Uses dependency injection for platform-specific services
 */
export const createValidationViewModel = (services: IPlatformServices) => {
  return (): ValidationViewModel => {
    // Local validation state
    const [phoneValidation, setPhoneValidation] = useState<PhoneFieldValidation>(
      createPhoneFieldValidation()
    )
    const [emailValidation, setEmailValidation] = useState<FieldValidation>(
      createFieldValidation()
    )
    const [addressValidation, setAddressValidation] = useState({
      street: createFieldValidation(),
      apartment: createFieldValidation(),
      city: createFieldValidation(),
      state: createFieldValidation(),
      zipCode: createFieldValidation(),
      isComplete: false,
    })
    const [paymentValidation, setPaymentValidation] = useState({
      cardNumber: createPaymentCardValidation(),
      expiryMonth: createFieldValidation(),
      expiryYear: createFieldValidation(),
      cvv: createFieldValidation(),
      isComplete: false,
      isExpiryValid: false,
    })
    const [formValidation, setFormValidation] = useState<FormValidation>({
      hasErrors: false,
      touchedFields: [],
      isFormValid: false,
      errors: {},
    })
    const [validationPreferences, setValidationPreferencesState] = useState<ValidationPreferences>({
      enableRealTimeValidation: true,
      phoneFormat: 'us',
      showValidationIcons: true,
      validationDelay: 300,
    })

    // Load preferences from storage on mount
    useEffect(() => {
      const loadPreferences = async () => {
        try {
          const stored = await services.storageService.getItem('validation-preferences')
          if (stored) {
            const parsed = JSON.parse(stored)
            const validated = ValidationPreferencesSchema.safeParse(parsed)
            if (validated.success) {
              setValidationPreferencesState(validated.data)
            }
          }
        } catch (error) {
          safeGetLogger()?.warn('Failed to load validation preferences', { error: String(error) })
        }
      }
      loadPreferences()
    }, [services.storageService])

    // Phone validation
    const validatePhone = useCallback((phone: string) => {
      try {
        const cleanPhone = phone ? phone.toString() : ''
        const rawValue = getPhoneDigits(cleanPhone)
        const isValid = isValidPhone(cleanPhone)
        let formatted = ''
        let error: string | null = null
        
        if (cleanPhone === '') {
          error = 'Phone number is required'
        } else if (!isValid) {
          error = 'Please enter a valid 10-digit phone number'
        } else {
          formatted = validationPreferences.phoneFormat === 'international' 
            ? formatPhone(cleanPhone)
            : formatPhone(cleanPhone)
        }

        const newValidation = createPhoneFieldValidation(
          cleanPhone,
          isValid,
          error,
          true,
          formatted,
          rawValue
        )
        
        setPhoneValidation(newValidation)
        updateFormValidation('phone', isValid, error)
      } catch (error) {
        const errorValidation = createPhoneFieldValidation(
          phone,
          false,
          'Please enter a valid phone number',
          true
        )
        setPhoneValidation(errorValidation)
        updateFormValidation('phone', false, 'Please enter a valid phone number')
      }
    }, [validationPreferences.phoneFormat])

    // Email validation  
    const validateEmail = useCallback((email: string) => {
      try {
        const isNullish = email == null || email === undefined
        const cleanEmail = email ? email.toString().toLowerCase().trim() : ''
        let isValid = false
        let error: string | null = null

        if (isNullish) {
          error = 'Please enter a valid email address'
        } else if (cleanEmail === '') {
          error = 'Email is required'
        } else if (!isValidEmail(cleanEmail) || cleanEmail.includes('..')) {
          error = 'Please enter a valid email address'
        } else {
          isValid = true
        }

        const newValidation = createFieldValidation(cleanEmail, isValid, error, true)
        setEmailValidation(newValidation)
        updateFormValidation('email', isValid, error)
      } catch (error) {
        const errorValidation = createFieldValidation(
          email,
          false,
          'Please enter a valid email address',
          true
        )
        setEmailValidation(errorValidation)
        updateFormValidation('email', false, 'Please enter a valid email address')
      }
    }, [])

    // Address validation
    const validateAddress = useCallback((address: Partial<AddressFields>) => {
      const newAddressValidation = { ...addressValidation }
      let hasAllRequired = true

      // Validate street
      if (address.street !== undefined) {
        const street = address.street.trim()
        const isValid = street.length >= 3
        const error = isValid ? null : 'Street address must be at least 3 characters'
        newAddressValidation.street = createFieldValidation(street, isValid, error, true)
        if (!isValid) hasAllRequired = false
        updateFormValidation('address.street', isValid, error)
      }

      // Validate apartment (optional)
      if (address.apartment !== undefined) {
        const apartment = address.apartment.trim()
        newAddressValidation.apartment = createFieldValidation(apartment, true, null, true)
      }

      // Validate city
      if (address.city !== undefined) {
        const city = address.city.trim()
        const isValid = city.length >= 2
        const error = isValid ? null : 'City must be at least 2 characters'
        newAddressValidation.city = createFieldValidation(city, isValid, error, true)
        if (!isValid) hasAllRequired = false
        updateFormValidation('address.city', isValid, error)
      }

      // Validate state
      if (address.state !== undefined) {
        const state = address.state.trim().toUpperCase()
        const isValid = state.length === 2
        const error = isValid ? null : 'Please use 2-letter state code (e.g., CA)'
        newAddressValidation.state = createFieldValidation(state, isValid, error, true)
        if (!isValid) hasAllRequired = false
        updateFormValidation('address.state', isValid, error)
      }

      // Validate ZIP code
      if (address.zipCode !== undefined) {
        const zipCode = address.zipCode.trim()
        const isValid = isValidZipCode(zipCode)
        const error = isValid ? null : 'Please enter a valid ZIP code'
        newAddressValidation.zipCode = createFieldValidation(zipCode, isValid, error, true)
        if (!isValid) hasAllRequired = false
        updateFormValidation('address.zipCode', isValid, error)
      }

      // Check if address is complete
      const isComplete = hasAllRequired &&
        newAddressValidation.street.isValid &&
        newAddressValidation.city.isValid &&
        newAddressValidation.state.isValid &&
        newAddressValidation.zipCode.isValid

      newAddressValidation.isComplete = isComplete
      setAddressValidation(newAddressValidation)
    }, [addressValidation])

    // Payment validation
    const validatePayment = useCallback((payment: Partial<PaymentFields>) => {
      const newPaymentValidation = { ...paymentValidation }
      let isComplete = true

      // Validate card number
      if (payment.cardNumber !== undefined) {
        const cardNumber = stripCardFormatting(payment.cardNumber)
        const cardType = detectCardType(cardNumber) as CreditCardType
        const isValidLength = isValidCardLength(cardNumber, cardType)
        const isValidLuhnCheck = isValidLuhn(cardNumber)
        const isValid = isValidLength && isValidLuhnCheck
        
        let error: string | null = null
        if (!isValid && cardNumber.length > 0) {
          error = 'Please enter a valid credit card number'
        }

        const last4 = cardNumber.length >= 4 ? cardNumber.slice(-4) : ''
        // Map card type to test-expected format
        const brandMap: Record<string, string> = {
          'amex': 'american_express',
          'visa': 'visa',
          'mastercard': 'mastercard'
        }
        const brand = cardType === 'other-card' ? undefined : brandMap[cardType] || cardType

        newPaymentValidation.cardNumber = createPaymentCardValidation(
          payment.cardNumber,
          isValid,
          error,
          true,
          brand,
          last4
        )
        if (!isValid) isComplete = false
        updateFormValidation('payment.cardNumber', isValid, error)
      }

      // Validate expiry month
      if (payment.expiryMonth !== undefined) {
        const month = payment.expiryMonth.trim()
        const monthNum = parseInt(month, 10)
        const isValid = monthNum >= 1 && monthNum <= 12
        const error = isValid ? null : 'Invalid month'
        
        newPaymentValidation.expiryMonth = createFieldValidation(month, isValid, error, true)
        if (!isValid) isComplete = false
        updateFormValidation('payment.expiryMonth', isValid, error)
      }

      // Validate expiry year
      if (payment.expiryYear !== undefined) {
        const year = payment.expiryYear.trim()
        const yearNum = parseInt(year, 10)
        const currentYear = new Date().getFullYear()
        const currentMonth = new Date().getMonth() + 1
        const monthNum = parseInt(newPaymentValidation.expiryMonth.value, 10) || 12
        
        let isValid = yearNum >= currentYear
        let error: string | null = null

        // If it's the current year, check the month too
        if (yearNum === currentYear && monthNum < currentMonth) {
          isValid = false
          error = 'Card has expired'
        } else if (yearNum < currentYear) {
          isValid = false
          error = 'Card has expired'
        } else if (year.length !== 4) {
          isValid = false
          error = 'Invalid year'
        }

        newPaymentValidation.expiryYear = createFieldValidation(year, isValid, error, true)
        if (!isValid) isComplete = false
        updateFormValidation('payment.expiryYear', isValid, error)
      }

      // Validate CVV
      if (payment.cvv !== undefined) {
        const cvv = payment.cvv.trim()
        
        // Determine card type from current payment data or stored state
        let cardType = newPaymentValidation.cardNumber.brand
        if (payment.cardNumber !== undefined) {
          const cardNumber = stripCardFormatting(payment.cardNumber)
          const detectedType = detectCardType(cardNumber) as CreditCardType
          const brandMap: Record<string, string> = {
            'amex': 'american_express',
            'visa': 'visa',
            'mastercard': 'mastercard'
          }
          cardType = detectedType === 'other-card' ? undefined : brandMap[detectedType] || detectedType
        }
        
        const expectedLength = cardType === 'american_express' ? 4 : 3
        const isValid = cvv.length === expectedLength && /^\d+$/.test(cvv)
        const error = isValid ? null : 'Invalid CVV'
        
        newPaymentValidation.cvv = createFieldValidation(cvv, isValid, error, true)
        if (!isValid) isComplete = false
        updateFormValidation('payment.cvv', isValid, error)
      }

      // Check expiry validity
      const isExpiryValid = newPaymentValidation.expiryMonth.isValid && 
                           newPaymentValidation.expiryYear.isValid

      // Check overall completion
      isComplete = isComplete &&
        newPaymentValidation.cardNumber.isValid &&
        newPaymentValidation.expiryMonth.isValid &&
        newPaymentValidation.expiryYear.isValid &&
        newPaymentValidation.cvv.isValid

      newPaymentValidation.isComplete = isComplete
      newPaymentValidation.isExpiryValid = isExpiryValid
      setPaymentValidation(newPaymentValidation)
    }, [paymentValidation])

    // Helper to update form validation state
    const updateFormValidation = useCallback((fieldName: string, _isValid: boolean, error: string | null) => {
      setFormValidation(prev => {
        const newTouchedFields = prev.touchedFields.includes(fieldName) 
          ? prev.touchedFields 
          : [...prev.touchedFields, fieldName]
        
        const newErrors = { ...prev.errors }
        if (error) {
          newErrors[fieldName] = error
        } else {
          delete newErrors[fieldName]
        }

        const hasErrors = Object.keys(newErrors).length > 0
        
        return {
          ...prev,
          touchedFields: newTouchedFields,
          errors: newErrors,
          hasErrors,
          isFormValid: !hasErrors && newTouchedFields.length > 0,
        }
      })
    }, [])

    // Form validation orchestration
    const validateForm = useCallback((formData: any) => {
      if (formData.email !== undefined) {
        validateEmail(formData.email)
      }
      if (formData.phone !== undefined) {
        validatePhone(formData.phone)
      }
      if (formData.address !== undefined) {
        validateAddress(formData.address)
      }
      if (formData.payment !== undefined) {
        validatePayment(formData.payment)
      }
    }, [validateEmail, validatePhone, validateAddress, validatePayment])

    const resetFormValidation = useCallback(() => {
      setPhoneValidation(createPhoneFieldValidation())
      setEmailValidation(createFieldValidation())
      setAddressValidation({
        street: createFieldValidation(),
        apartment: createFieldValidation(),
        city: createFieldValidation(),
        state: createFieldValidation(),
        zipCode: createFieldValidation(),
        isComplete: false,
      })
      setPaymentValidation({
        cardNumber: createPaymentCardValidation(),
        expiryMonth: createFieldValidation(),
        expiryYear: createFieldValidation(),
        cvv: createFieldValidation(),
        isComplete: false,
        isExpiryValid: false,
      })
      setFormValidation({
        hasErrors: false,
        touchedFields: [],
        isFormValid: false,
        errors: {},
      })
    }, [])

    const getValidationSummary = useCallback((): ValidationSummary => {
      const errors: Array<{ field: string; message: string }> = []
      let validFields = 0
      let totalFields = 0

      // Check each field type
      const fields = [
        { name: 'phone', validation: phoneValidation },
        { name: 'email', validation: emailValidation },
        { name: 'address.street', validation: addressValidation.street },
        { name: 'address.city', validation: addressValidation.city },
        { name: 'address.state', validation: addressValidation.state },
        { name: 'address.zipCode', validation: addressValidation.zipCode },
        { name: 'payment.cardNumber', validation: paymentValidation.cardNumber },
        { name: 'payment.expiryMonth', validation: paymentValidation.expiryMonth },
        { name: 'payment.expiryYear', validation: paymentValidation.expiryYear },
        { name: 'payment.cvv', validation: paymentValidation.cvv },
      ]

      for (const field of fields) {
        if (field.validation.touched) {
          totalFields++
          if (field.validation.isValid) {
            validFields++
          } else if (field.validation.error) {
            errors.push({
              field: field.name,
              message: field.validation.error,
            })
          }
        }
      }

      return {
        totalFields,
        validFields,
        invalidFields: totalFields - validFields,
        errors,
        isValid: errors.length === 0 && totalFields > 0,
      }
    }, [phoneValidation, emailValidation, addressValidation, paymentValidation])

    // Field utilities
    const getFieldError = useCallback((fieldName: string): string | null => {
      switch (fieldName) {
        case 'phone': return phoneValidation.error
        case 'email': return emailValidation.error
        case 'address.street': return addressValidation.street.error
        case 'address.city': return addressValidation.city.error
        case 'address.state': return addressValidation.state.error
        case 'address.zipCode': return addressValidation.zipCode.error
        case 'payment.cardNumber': return paymentValidation.cardNumber.error
        case 'payment.expiryMonth': return paymentValidation.expiryMonth.error
        case 'payment.expiryYear': return paymentValidation.expiryYear.error
        case 'payment.cvv': return paymentValidation.cvv.error
        default: return null
      }
    }, [phoneValidation, emailValidation, addressValidation, paymentValidation])

    const isFieldValid = useCallback((fieldName: string): boolean => {
      switch (fieldName) {
        case 'phone': return phoneValidation.isValid
        case 'email': return emailValidation.isValid
        case 'address.street': return addressValidation.street.isValid
        case 'address.city': return addressValidation.city.isValid
        case 'address.state': return addressValidation.state.isValid
        case 'address.zipCode': return addressValidation.zipCode.isValid
        case 'payment.cardNumber': return paymentValidation.cardNumber.isValid
        case 'payment.expiryMonth': return paymentValidation.expiryMonth.isValid
        case 'payment.expiryYear': return paymentValidation.expiryYear.isValid
        case 'payment.cvv': return paymentValidation.cvv.isValid
        default: return false
      }
    }, [phoneValidation, emailValidation, addressValidation, paymentValidation])

    const isFieldTouched = useCallback((fieldName: string): boolean => {
      switch (fieldName) {
        case 'phone': return phoneValidation.touched
        case 'email': return emailValidation.touched
        case 'address.street': return addressValidation.street.touched
        case 'address.city': return addressValidation.city.touched
        case 'address.state': return addressValidation.state.touched
        case 'address.zipCode': return addressValidation.zipCode.touched
        case 'payment.cardNumber': return paymentValidation.cardNumber.touched
        case 'payment.expiryMonth': return paymentValidation.expiryMonth.touched
        case 'payment.expiryYear': return paymentValidation.expiryYear.touched
        case 'payment.cvv': return paymentValidation.cvv.touched
        default: return false
      }
    }, [phoneValidation, emailValidation, addressValidation, paymentValidation])

    // Preferences management
    const setValidationPreferences = useCallback(async (preferences: Partial<ValidationPreferences>) => {
      const newPreferences = { ...validationPreferences, ...preferences }
      setValidationPreferencesState(newPreferences)
      
      try {
        await services.storageService.setItem(
          'validation-preferences',
          JSON.stringify(newPreferences)
        )
      } catch (error) {
        safeGetLogger()?.warn('Failed to save validation preferences', { error: String(error) })
      }
    }, [validationPreferences, services.storageService])

    return {
      // State
      phoneValidation,
      emailValidation,
      addressValidation,
      paymentValidation,
      formValidation,
      validationPreferences,
      
      // Actions
      validatePhone,
      validateEmail,
      validateAddress,
      validatePayment,
      validateForm,
      resetFormValidation,
      getValidationSummary,
      getFieldError,
      isFieldValid,
      isFieldTouched,
      setValidationPreferences,
    }
  }
}